import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger, Input } from "@nextui-org/react";
import { IoChevronDown, IoClose, IoSearch } from "react-icons/io5";
import { MdCheck } from "react-icons/md";
import { useTranslation } from "react-i18next";

// Above this many options the list gets a search field.
const SEARCHABLE_FROM = 8;

// Single-select dropdown styled to sit next to CalendarDropdown in the navbar.
// `options` are { key, label }; the caller supplies the "all" entry so it can
// name it ("All departments", "All directions", ...).
const FilterDropdown = ({
  icon: Icon,
  placeholder = "",
  options = [],
  value = "all",
  onChange = () => {},
  triggerClassName = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((option) => option.key === value);
  const isActive = value && value !== "all";
  const isSearchable = options.length >= SEARCHABLE_FROM;

  const visibleOptions = useMemo(() => {
    if (!query.trim()) return options;
    const needle = query.trim().toLowerCase();
    return options.filter((option) => t(option.label).toLowerCase().includes(needle));
  }, [options, query, t]);

  const select = (key) => {
    onChange(key);
    setIsOpen(false);
    setQuery("");
  };

  // The clear button sits inside the popover trigger, so its events must not
  // bubble up or clicking the cross would also open the list. Propagation only:
  // preventDefault here would stop the button's own click, including the one the
  // browser synthesises from Enter / Space.
  const clear = (event) => {
    event.stopPropagation();
    onChange("all");
    setIsOpen(false);
  };
  const swallow = (event) => event.stopPropagation();

  return (
    <Popover
      placement="bottom-start"
      showArrow={false}
      offset={10}
      shouldCloseOnBlur
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setQuery("");
      }}
    >
      <PopoverTrigger>
        <div
          className={`bg-white border duration-300 px-[12px] h-[40px] rounded-lg flex items-center cursor-pointer gap-[8px] ${
            isActive
              ? "border-primary text-primary"
              : "border-[#E5E5E5] hover:border-[#A3A3A3] text-[#404040]"
          } ${triggerClassName}`}
        >
          {Icon && (
            <Icon
              size={18}
              className={`shrink-0 ${isActive ? "text-primary" : "text-[#737373]"}`}
            />
          )}
          <span className="text-[14px] whitespace-nowrap max-w-[160px] truncate">
            {selected ? t(selected.label) : t(placeholder)}
          </span>
          {isActive ? (
            <button
              type="button"
              aria-label={t("Clear filter")}
              title={t("Clear filter")}
              onClick={clear}
              onPointerDown={swallow}
              onMouseDown={swallow}
              onKeyDown={swallow}
              className="shrink-0 -mr-[4px] p-[2px] rounded-full text-primary hover:bg-primary/10 duration-200"
            >
              <IoClose size={16} />
            </button>
          ) : (
            <IoChevronDown
              size={16}
              className={`shrink-0 text-[#737373] transition-transform duration-300 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[240px] p-0">
        <div className="w-full py-2">
          {isSearchable && (
            <div className="px-2 pb-2">
              <Input
                size="sm"
                variant="bordered"
                autoFocus
                placeholder={t("Search")}
                value={query}
                onValueChange={setQuery}
                startContent={<IoSearch className="text-[#737373]" />}
              />
            </div>
          )}
          <div className="max-h-[300px] overflow-y-auto">
            {visibleOptions.length === 0 && (
              <div className="px-4 py-3 text-[13px] text-gray-400">
                {t("No records to display")}
              </div>
            )}
            {visibleOptions.map((option) => {
              const isSelected = option.key === value;
              return (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => select(option.key)}
                  className={`w-full text-left px-4 py-2 text-[14px] flex items-center justify-between gap-3 hover:bg-black/5 duration-200 ${
                    isSelected ? "text-primary font-medium" : "text-[#404040]"
                  }`}
                >
                  <span className="truncate">{t(option.label)}</span>
                  {isSelected && (
                    <MdCheck
                      size={16}
                      className="shrink-0"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FilterDropdown;
